import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Reconcile bookkeeping, deliberately at MODULE scope.
 *
 * A section morph can destroy and recreate <gbu-cart-drawer-extras>, which resets any
 * per-instance guard. That is the difference between "retry once" and "retry forever", so
 * this state has to outlive the element.
 */
const reconcileGuard = {
  /** @type {Set<string>} Cart states we have already issued a request for. */
  acted: new Set(),
  /** @type {number} Requests issued this page load. */
  total: 0,
};

/** Backstop: no cart state should ever need more than a handful of corrections. */
const MAX_RECONCILE_REQUESTS = 8;

/**
 * Line item key of the bundle modal the shopper currently has open, at module scope so it
 * survives a section morph. Changing quantity inside the modal re-renders the drawer from
 * the server, which restores `hidden` and drops the open class — without this the modal
 * closed itself on every single step.
 * @type {string | null}
 */
let openModalKey = null;

/**
 * Behaviour layer for the GBU cart drawer.
 *
 * Owns only what Horizon does not already do:
 *   - the bundle contents modal
 *   - the shipping protection toggle
 *   - reconciling collection-based free gifts
 *   - the complementary product upsell row
 *   - the drawer-wide "updating" state during our own mutations
 *
 * Quantity changes and line removal are deliberately left to Horizon's
 * `cart-items-component`, which already owns `/cart/change.js` + `morphSection`.
 *
 * All listeners are delegated from this element so they survive `morphSection`
 * replacing the drawer's inner markup.
 */
class GbuCartDrawerExtras extends HTMLElement {
  /** @type {boolean} Guards the gift reconciler against re-entry. */
  #giftBusy = false;

  /** @type {string | null} Cache key for the last upsell fetch. */
  #upsellKey = null;

  /** @type {Array<Record<string, any>> | null} Cached upsell payload. */
  #upsellProducts = null;

  /** @type {number} Nesting depth of in-flight mutations, so the state clears once. */
  #pending = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #updatingWatchdog;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('keydown', this.#onDocumentKeydown);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    this.#afterRender();
  }

  disconnectedCallback() {
    clearTimeout(this.#updatingWatchdog);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /* ----------------------------------------------------------------------- */
  /* Wiring                                                                   */
  /* ----------------------------------------------------------------------- */

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const bundleOpen = target.closest('[data-gbucd-bundle-open]');
    if (bundleOpen instanceof HTMLElement) {
      event.preventDefault();
      this.#openBundle(bundleOpen.dataset.gbucdBundleOpen ?? '');
      return;
    }

    if (target.closest('[data-gbucd-bundle-close]')) {
      event.preventDefault();
      this.#closeBundles();
      return;
    }

    // Clicking the dimmed backdrop closes the modal.
    if (target.matches('[data-gbucd-bundle-modal]')) {
      this.#closeBundles();
      return;
    }

    const protection = target.closest('[data-gbucd-protection]');
    if (protection instanceof HTMLElement) {
      event.preventDefault();
      this.#toggleProtection(protection);
      return;
    }

    const upsellAdd = target.closest('[data-gbucd-upsell-add]');
    if (upsellAdd instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#addUpsell(upsellAdd);
      return;
    }

    const qtyProxy = target.closest('[data-gbucd-qty]');
    if (qtyProxy instanceof HTMLElement) {
      event.preventDefault();
      this.#proxyQuantity(qtyProxy.dataset.gbucdQty ?? '', qtyProxy.dataset.dir ?? 'up');
      return;
    }

    const dismiss = target.closest('[data-gbucd-combine-dismiss]');
    if (dismiss instanceof HTMLElement) {
      event.preventDefault();
      this.#dismissCombo(dismiss.dataset.gbucdCombineDismiss ?? '');
      return;
    }

    const combine = target.closest('[data-gbucd-combine]');
    if (combine instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#combine(combine);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    // The bundle thumbnail is a div with role="button", so it needs manual key support.
    const bundleOpen = target.closest('[data-gbucd-bundle-open]');
    if (bundleOpen instanceof HTMLElement && bundleOpen.getAttribute('role') === 'button') {
      event.preventDefault();
      this.#openBundle(bundleOpen.dataset.gbucdBundleOpen ?? '');
    }
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.querySelector('.gbucd-modal-overlay.gbucd-is-open')) return;

    // Swallow the key so Horizon's dialog doesn't close the whole drawer behind the modal.
    event.stopPropagation();
    event.preventDefault();
    this.#closeBundles();
  };

  #onCartUpdate = () => {
    // Let `cart-items-component` finish morphing before we read the new markup.
    setTimeout(() => this.#afterRender(), 0);
  };

  #afterRender() {
    // A fresh render means any previous request has landed. If nothing of ours is in flight,
    // clear the loading state — this catches a class stranded by an element swap.
    if (this.#pending === 0) this.#paintUpdating();

    // Re-open the bundle modal the shopper was using. The re-render replaced it with the
    // server's closed copy, so without this every quantity step slammed it shut.
    if (openModalKey) {
      const key = openModalKey;
      const stillExists = Array.from(this.querySelectorAll('[data-gbucd-bundle-modal]')).some(
        (modal) => modal instanceof HTMLElement && modal.dataset.gbucdBundleModal === key
      );

      if (stillExists) this.#openBundle(key, false);
      else openModalKey = null; // the line was removed while the modal was open
    }

    this.#applyDismissals();
    this.#reconcile();
    this.#loadUpsell();
  }

  /* ----------------------------------------------------------------------- */
  /* Bundle modal                                                             */
  /* ----------------------------------------------------------------------- */

  /**
   * @param {string} key
   * @param {boolean} [animate] False when restoring after a re-render, so it does not
   *   flash open again on every quantity step.
   */
  #openBundle(key, animate = true) {
    if (!key) return;

    // Line keys contain a colon, so match on the dataset value rather than building an
    // attribute selector that would need escaping.
    const modal = Array.from(this.querySelectorAll('[data-gbucd-bundle-modal]')).find(
      (candidate) => candidate instanceof HTMLElement && candidate.dataset.gbucdBundleModal === key
    );

    if (!(modal instanceof HTMLElement)) return;

    this.#closeBundles();
    openModalKey = key;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');

    if (animate) {
      // Next frame, so the transition has a start state to animate from.
      requestAnimationFrame(() => modal.classList.add('gbucd-is-open'));

      const cancel = modal.querySelector('[data-gbucd-bundle-close]');
      if (cancel instanceof HTMLElement) cancel.focus();
    } else {
      modal.classList.add('gbucd-is-open');
    }
  }

  /**
   * Drives a bundle row's quantity from inside its modal.
   *
   * The modal only proxies: it clicks the row's real Horizon +/- button, so the quantity
   * change goes through `cart-quantity-selector-component` and `cart-items-component`
   * exactly as it would from the row, with no duplicate cart logic here. The row's selector
   * has to stay in the row for Horizon's DOM-order ref indexing, hence the indirection.
   *
   * @param {string} key Line item key of the bundle row.
   * @param {string} direction 'up' or 'down'.
   */
  #proxyQuantity(key, direction) {
    if (!key) return;

    // Match on the dataset rather than an attribute selector: line keys contain a colon.
    const row = Array.from(this.querySelectorAll('.gbucd-item')).find(
      (candidate) => candidate instanceof HTMLElement && candidate.dataset.key === key
    );

    if (!(row instanceof HTMLElement)) return;

    const button = row.querySelector(direction === 'down' ? '.quantity-minus' : '.quantity-plus');
    if (button instanceof HTMLElement) button.click();
  }

  /** @param {boolean} [forget] False when merely re-rendering, so the modal reopens after. */
  #closeBundles(forget = true) {
    if (forget) openModalKey = null;

    for (const modal of this.querySelectorAll('[data-gbucd-bundle-modal]')) {
      if (!(modal instanceof HTMLElement) || modal.hidden) continue;

      modal.classList.remove('gbucd-is-open');
      modal.setAttribute('aria-hidden', 'true');

      const hide = () => {
        modal.hidden = true;
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) hide();
      else setTimeout(hide, 250);
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Shipping protection                                                      */
  /* ----------------------------------------------------------------------- */

  /** @param {HTMLElement} button */
  async #toggleProtection(button) {
    const wasOn = button.getAttribute('aria-checked') === 'true';
    const variantId = Number(button.dataset.variantId ?? 0);
    const line = Number(button.dataset.line ?? 0);

    if (!variantId) return;

    // Optimistic flip so the switch feels instant; rolled back if the request fails.
    button.setAttribute('aria-checked', String(!wasOn));

    // Turning it off is a deliberate choice, so auto-add must not undo it. Turning it back
    // on clears that, and either way the session remembers.
    this.#setProtectionOptOut(wasOn);

    try {
      if (wasOn) {
        if (line > 0) await this.#changeLine(line, 0);
      } else {
        await this.#addVariant(variantId, 1);
      }
    } catch (error) {
      button.setAttribute('aria-checked', String(wasOn));
      this.#setProtectionOptOut(!wasOn);
      console.error('[gbu-cart-drawer] shipping protection toggle failed', error);
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Gift with purchase                                                       */
  /* ----------------------------------------------------------------------- */

  /**
   * Brings the cart in line with what Liquid decided: gift lines that should or should not
   * be there, and the shipping protection line.
   *
   * Liquid owns every decision; this performs at most ONE mutation per pass. The resulting
   * re-render triggers another pass, so several changes settle one at a time. A signature of
   * the whole state guards against looping: if a mutation leaves the state unchanged (a
   * silent failure, say) the next pass sees the same signature and stops.
   */
  async #reconcile() {
    if (this.#giftBusy) return;

    const rules = this.#readJson('script[data-gbucd-gift-rules]', []);
    const protection = this.#readJson('script[data-gbucd-protection-state]', null);

    if (!Array.isArray(rules)) return;

    const signature = JSON.stringify({ rules, protection });

    // Never act on the same cart state twice. If a request succeeds but does not change the
    // state — an out-of-stock reward, a variant the storefront refuses — this is what stops
    // it being attempted again on the next render.
    if (reconcileGuard.acted.has(signature)) return;

    // Every change is collected into ONE /cart/update.js call. Doing them one at a time
    // meant a milestone swap (drop the old tier's rewards, add the new tier's two) cost four
    // round trips, each re-rendering the whole header section — which reads as a hang.
    // `updates` is keyed by variant id: a quantity of 0 removes, and a variant not yet in
    // the cart is added, so adds and removes travel together.
    /** @type {Map<number, number>} */
    const updates = new Map();

    // Decide the final quantity for every variant BEFORE diffing against the cart.
    //
    // Doing it per-rule instead caused an infinite loop whenever two milestones shared a
    // product: the satisfied rule emitted nothing (it had nothing to do), so the unmet rule
    // was free to remove a product the met rule still wanted — then the met rule re-added
    // it on the next render, forever. A rule must defend its product even when it is
    // already in the cart, so "some rule wants it" is resolved first and wins.
    /** @type {Map<number, boolean>} */
    const wanted = new Map();
    /** @type {Map<number, boolean>} */
    const present = new Map();

    for (const rule of rules) {
      if (!rule.variantId) continue;
      wanted.set(rule.variantId, (wanted.get(rule.variantId) ?? false) || Boolean(rule.met));
      present.set(rule.variantId, (present.get(rule.variantId) ?? false) || rule.line > 0);
    }

    for (const [variantId, isWanted] of wanted) {
      const inCart = present.get(variantId) ?? false;
      if (isWanted && !inCart) updates.set(variantId, 1);
      else if (!isWanted && inCart) updates.set(variantId, 0);
    }

    /**
     * @param {number} variantId
     * @param {number} quantity
     */
    const want = (variantId, quantity) => {
      if (!variantId) return;
      const existing = updates.get(variantId);
      updates.set(variantId, existing === undefined ? quantity : Math.max(existing, quantity));
    };

    if (protection?.enabled && protection.variantId) {
      // A protection line left in an otherwise empty cart is dropped, so the drawer can fall
      // through to its empty state instead of looking occupied.
      if (protection.inCart && protection.paidItems === 0) {
        updates.set(protection.variantId, 0);
      } else if (
        !protection.inCart &&
        protection.defaultOn &&
        protection.paidItems > 0 &&
        !this.#protectionOptedOut()
      ) {
        want(protection.variantId, 1);
      }
    }

    if (updates.size === 0) return;

    if (reconcileGuard.total >= MAX_RECONCILE_REQUESTS) {
      console.warn(
        '[gbu-cart-drawer] stopping: the cart still wants changes after ' +
          `${MAX_RECONCILE_REQUESTS} attempts. Usually a reward product that cannot be added ` +
          '(out of stock, or not available on the Online Store sales channel). Pending:',
        Object.fromEntries(updates)
      );
      return;
    }

    reconcileGuard.acted.add(signature);
    reconcileGuard.total += 1;

    this.#giftBusy = true;
    try {
      await this.#updateCart(Object.fromEntries(updates));
    } catch (error) {
      console.error('[gbu-cart-drawer] cart reconcile failed', error);
    } finally {
      this.#giftBusy = false;
    }
  }

  /**
   * Applies several quantity changes in a single request.
   * @param {Record<string, number>} updates Variant id to desired quantity.
   */
  #updateCart(updates) {
    const url = window.Theme?.routes?.cart_update_url ?? '/cart/update.js';
    return this.#request(url, { updates });
  }

  /**
   * @param {string} selector
   * @param {any} fallback
   */
  #readJson(selector, fallback) {
    const node = this.querySelector(selector);
    if (!node) return fallback;

    try {
      return JSON.parse(node.textContent || '');
    } catch {
      return fallback;
    }
  }

  /** @returns {boolean} Whether the shopper explicitly turned protection off this session. */
  #protectionOptedOut() {
    try {
      return sessionStorage.getItem('gbucd-protection-optout') === '1';
    } catch {
      return false;
    }
  }

  /** @param {boolean} optedOut */
  #setProtectionOptOut(optedOut) {
    try {
      if (optedOut) sessionStorage.setItem('gbucd-protection-optout', '1');
      else sessionStorage.removeItem('gbucd-protection-optout');
    } catch {
      // Private browsing can refuse sessionStorage; the toggle still works for this render.
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Combine loose items into a bundle                                        */
  /* ----------------------------------------------------------------------- */

  /**
   * Swaps the matched loose lines for the bundle product.
   *
   * Ordering matters and is the whole safety story: the bundle is ADDED FIRST. If that call
   * fails, nothing has been removed and the cart is exactly as it was. Only once the bundle
   * is definitely in the cart are the component lines reduced, addressed by line item key so
   * shifting indexes cannot make us touch the wrong line.
   *
   * @param {HTMLButtonElement} button
   */
  async #combine(button) {
    const id = button.dataset.gbucdCombine ?? '';
    const combo = this.#combos().find((entry) => String(entry.id) === String(id));

    if (!combo || !combo.variantId || button.disabled) return;

    button.disabled = true;

    try {
      // Step 1 — add the bundle. Nothing is destructive until this succeeds.
      await this.#addVariant(combo.variantId, 1, false);
    } catch (error) {
      console.error('[gbu-cart-drawer] combine failed while adding the bundle; cart unchanged', error);
      button.disabled = false;
      return;
    }

    // Step 2 — consume the loose lines. Re-render only after the last one.
    const lines = Array.isArray(combo.lines) ? combo.lines : [];

    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      const isLast = index === lines.length - 1;

      if (!line || typeof line.key !== 'string') continue;

      try {
        await this.#changeLineByKey(line.key, Number(line.qty) || 0, isLast);
      } catch (error) {
        // The bundle is already in the cart, so stop rather than press on. The shopper can
        // see exactly what is there and adjust; nothing has been silently lost.
        console.error('[gbu-cart-drawer] combine partially applied; bundle added but a line was not reduced', error);
        this.#refresh();
        return;
      }
    }

    // No component lines to reduce still needs the drawer to catch up.
    if (lines.length === 0) this.#refresh();
  }

  /** @returns {Array<{id: number, variantId: number, lines: Array<{key: string, qty: number}>}>} */
  #combos() {
    const script = this.querySelector('script[data-gbucd-combos]');
    if (!script) return [];

    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Hides a suggestion for the rest of the session.
   * @param {string} id
   */
  #dismissCombo(id) {
    if (!id) return;

    try {
      sessionStorage.setItem(`gbucd-combine-dismissed-${id}`, '1');
    } catch {
      // Private browsing can refuse sessionStorage; hiding for this render is still fine.
    }

    this.#applyDismissals();
  }

  #applyDismissals() {
    for (const card of this.querySelectorAll('[data-gbucd-combine-card]')) {
      if (!(card instanceof HTMLElement)) continue;

      let dismissed = false;
      try {
        dismissed = sessionStorage.getItem(`gbucd-combine-dismissed-${card.dataset.gbucdCombineCard}`) === '1';
      } catch {
        dismissed = false;
      }

      card.hidden = dismissed;
    }
  }

  /** Pulls a fresh copy of the section when a response could not carry one. */
  #refresh() {
    const sectionId = this.#sectionId;
    if (!sectionId) return;

    fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`)
      .then((response) => response.text())
      .then((html) => {
        document.dispatchEvent(
          new CartUpdateEvent({}, 'gbu-cart-drawer', {
            source: 'gbu-cart-drawer',
            itemCount: this.#renderedItemCount(html),
            sections: { [sectionId]: html },
          })
        );
      })
      .catch((error) => console.error('[gbu-cart-drawer] refresh failed', error));
  }

  /**
   * @param {string} html
   * @returns {number}
   */
  #renderedItemCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const rendered = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
    return rendered ? parseInt(rendered, 10) : 0;
  }

  /* ----------------------------------------------------------------------- */
  /* Upsell                                                                   */
  /* ----------------------------------------------------------------------- */

  async #loadUpsell() {
    const container = this.querySelector('[data-gbucd-upsell]');
    if (!(container instanceof HTMLElement)) return;

    const list = container.querySelector('[data-gbucd-upsell-list]');
    const seed = container.dataset.seedProduct;
    const endpoint = container.dataset.recommendationsUrl;

    // Liquid renders 0 when the cart holds nothing purchasable, and the string "0" is
    // truthy, so compare numerically rather than relying on falsiness.
    if (!(list instanceof HTMLElement) || !endpoint || !(Number(seed) > 0)) {
      container.hidden = true;
      return;
    }

    const limit = Number(container.dataset.limit) || 6;
    const key = `${seed}:${limit}`;

    if (this.#upsellKey !== key || !this.#upsellProducts) {
      // "complementary" only returns anything if the merchant has curated it in Search &
      // Discovery. Fall back to "related", which Shopify generates automatically, so the row
      // still fills in rather than silently disappearing.
      const intents = ['complementary', 'related'];
      /** @type {Array<Record<string, any>>} */
      let found = [];

      try {
        for (const intent of intents) {
          const url = `${endpoint}.json?product_id=${encodeURIComponent(seed)}&limit=${limit}&intent=${intent}`;
          const response = await fetch(url);
          if (!response.ok) continue;

          const data = await response.json();
          const products = Array.isArray(data.products) ? data.products : [];

          if (products.length > 0) {
            found = products;
            break;
          }
        }
      } catch (error) {
        console.error('[gbu-cart-drawer] upsell fetch failed', error);
        container.hidden = true;
        return;
      }

      if (found.length === 0) {
        console.info(
          `[gbu-cart-drawer] no recommendations for product ${seed}. Set complementary or ` +
            'related products in Search & Discovery, or the row stays hidden.'
        );
      }

      this.#upsellProducts = found;
      this.#upsellKey = key;
    }

    this.#renderUpsell(container, list);
  }

  /**
   * @param {HTMLElement} container
   * @param {HTMLElement} list
   */
  #renderUpsell(container, list) {
    const inCart = new Set(
      (container.dataset.cartProducts ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );

    const moneyFormat = container.dataset.moneyFormat ?? '${{amount}}';
    const products = (this.#upsellProducts ?? []).filter((product) => !inCart.has(String(product.id)));

    if (products.length === 0) {
      container.hidden = true;
      list.replaceChildren();
      return;
    }

    const fragment = document.createDocumentFragment();

    for (const product of products) {
      const variant = product.variants?.[0];
      if (!variant) continue;

      const card = document.createElement('div');
      card.className = 'gbucd-up-card';

      if (product.featured_image) {
        const image = document.createElement('img');
        image.className = 'gbucd-up-card__img';
        image.src = `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=264`;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 132;
        image.height = 132;
        card.appendChild(image);
      }

      const name = document.createElement('a');
      name.className = 'gbucd-up-card__name';
      name.href = product.url ?? '#';
      name.textContent = product.title ?? '';
      card.appendChild(name);

      const foot = document.createElement('div');
      foot.className = 'gbucd-up-card__foot';

      const price = document.createElement('span');
      price.className = 'gbucd-up-card__price';
      price.textContent = formatMoney(variant.price, moneyFormat);
      foot.appendChild(price);

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'gbucd-up-card__add';
      add.textContent = '+';
      add.setAttribute('data-gbucd-upsell-add', '');
      add.setAttribute('data-variant-id', String(variant.id));
      add.setAttribute('aria-label', `Add ${product.title ?? ''}`);
      if (variant.available === false) add.disabled = true;
      foot.appendChild(add);

      card.appendChild(foot);
      fragment.appendChild(card);
    }

    list.replaceChildren(fragment);
    container.hidden = false;
  }

  /** @param {HTMLButtonElement} button */
  async #addUpsell(button) {
    const variantId = Number(button.dataset.variantId ?? 0);
    if (!variantId || button.disabled) return;

    button.disabled = true;
    try {
      await this.#addVariant(variantId, 1);
    } catch (error) {
      console.error('[gbu-cart-drawer] upsell add failed', error);
      button.disabled = false;
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Cart plumbing                                                            */
  /* ----------------------------------------------------------------------- */

  /** @returns {string} The section the drawer lives in (Horizon renders it inside the header). */
  get #sectionId() {
    const host = this.querySelector('cart-items-component');
    return host instanceof HTMLElement ? (host.dataset.sectionId ?? '') : '';
  }

  /**
   * @param {number} variantId
   * @param {number} quantity
   * @param {boolean} [publish]
   */
  #addVariant(variantId, quantity, publish = true) {
    const url = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
    return this.#request(url, { items: [{ id: variantId, quantity }] }, publish);
  }

  /**
   * @param {number} line
   * @param {number} quantity
   */
  #changeLine(line, quantity) {
    const url = window.Theme?.routes?.cart_change_url ?? '/cart/change.js';
    return this.#request(url, { line, quantity });
  }

  /**
   * Sets a line's quantity by its line item key. Keys are stable across mutations, whereas
   * line indexes shift as soon as anything is added or removed.
   *
   * @param {string} key
   * @param {number} quantity
   * @param {boolean} [publish]
   */
  #changeLineByKey(key, quantity, publish = true) {
    const url = window.Theme?.routes?.cart_change_url ?? '/cart/change.js';
    return this.#request(url, { id: key, quantity }, publish);
  }

  /**
   * Performs a cart mutation and republishes it through Horizon's own event, so
   * `cart-items-component` morphs the drawer and `cart-icon` updates the bubble.
   *
   * @param {string} url
   * @param {Record<string, any>} payload
   * @param {boolean} [publish] Pass false for intermediate steps of a multi-call
   *   operation, so the drawer re-renders once at the end instead of flickering per call.
   */
  async #request(url, payload, publish = true) {
    const sectionId = this.#sectionId;
    this.#setUpdating(true);

    try {
      const body = JSON.stringify({
        ...payload,
        ...(publish ? { sections: sectionId, sections_url: window.location.pathname } : {}),
      });

      const response = await fetch(url, fetchConfig('json', { body }));
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Cart request failed');
      }

      if (publish) this.#publish(data, sectionId);
      return data;
    } finally {
      this.#setUpdating(false);
    }
  }

  /**
   * @param {Record<string, any>} data
   * @param {string} sectionId
   */
  #publish(data, sectionId) {
    const sections = data.sections;

    // `/cart/change.js` returns the whole cart; `/cart/add.js` returns only the new line,
    // so fall back to the count Liquid rendered into the section, as Horizon does.
    let itemCount = typeof data.item_count === 'number' ? data.item_count : null;

    if (itemCount === null && sections?.[sectionId]) {
      const parsed = new DOMParser().parseFromString(sections[sectionId], 'text/html');
      const rendered = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
      itemCount = rendered ? parseInt(rendered, 10) : 0;
    }

    document.dispatchEvent(
      new CartUpdateEvent(data, 'gbu-cart-drawer', {
        source: 'gbu-cart-drawer',
        itemCount: itemCount ?? 0,
        sections,
      })
    );
  }

  /** @param {boolean} active */
  #setUpdating(active) {
    this.#pending = Math.max(0, this.#pending + (active ? 1 : -1));
    this.#paintUpdating();

    clearTimeout(this.#updatingWatchdog);

    if (this.#pending > 0) {
      // Last line of defence. A morph can detach this element while a request is in flight,
      // and a detached element's closest() finds nothing — which used to leave the drawer
      // showing its loading state forever. Never let that outlast the request by much.
      this.#updatingWatchdog = setTimeout(() => {
        this.#pending = 0;
        this.#paintUpdating();
      }, 15000);
    }
  }

  /**
   * Reflects the pending count onto the drawer.
   *
   * Resolves the root from the document when this element is no longer attached, so the
   * loading class is always cleared from the drawer the shopper is actually looking at.
   */
  #paintUpdating() {
    const root = this.closest('.gbucd') ?? document.querySelector('.gbucd');
    if (root instanceof HTMLElement) root.classList.toggle('gbucd--updating', this.#pending > 0);
  }
}

/**
 * Formats a price in the shop's subunits using a Shopify money format string.
 *
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const value = typeof cents === 'number' ? cents : parseInt(String(cents), 10) || 0;
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholder);

  if (!match) return format;

  /**
   * @param {number} precision
   * @param {string} thousands
   * @param {string} decimal
   */
  const build = (precision, thousands, decimal) => {
    const fixed = (value / 100).toFixed(precision);
    const [whole = '0', fraction = ''] = fixed.split('.');
    const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return precision > 0 ? `${grouped}${decimal}${fraction}` : grouped;
  };

  let amount;
  switch (match[1]) {
    case 'amount_no_decimals':
      amount = build(0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      amount = build(2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      amount = build(0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      amount = build(2, "'", '.');
      break;
    case 'amount_with_space_separator':
      amount = build(2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      amount = build(0, ' ', ',');
      break;
    default:
      amount = build(2, ',', '.');
      break;
  }

  return format.replace(placeholder, amount);
}

if (!customElements.get('gbu-cart-drawer-extras')) {
  customElements.define('gbu-cart-drawer-extras', GbuCartDrawerExtras);
}
